// Client side of the ai-analysis async-poll contract (no DOM — unit-testable).
//
// The server never blocks on Glean any more: POST answers 200 with the result when
// it is cached, or 202 { running: true } after kicking the run in the BACKGROUND —
// because the public URL sits behind an nginx ingress whose proxy-read-timeout is
// far shorter than an agentic Glean run. This module drives the client's half:
// start once, then poll the cheap GET probe until the cache fills, and only then
// fetch the result. Polling is idempotent by construction — the server's pending
// map dedups, and this loop calls start() exactly once regardless.
const defaultSleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Run one analysis to completion.
 *
 * @param {() => Promise<object>} start POST ai-analysis (with force when re-running):
 *   resolves to the result (cached) or { running: true } (202 — run kicked off).
 * @param {() => Promise<object>} probe GET ai-analysis: { cached, running, error }.
 * @param {() => Promise<object>} fetchResult POST again once cached — a free read.
 * @param {number} intervalMs poll cadence (~5s: fast enough to feel live, gentle
 *   enough that a wall of open cards doesn't hammer the API).
 * @param {(ms:number) => Promise<void>} sleep injectable for tests.
 * @returns the analysis result. A result we watched being generated is reported
 *   with cached:false — from the user's point of view it was made just now.
 * @throws when the background run dies (probe: not cached, not running), carrying
 *   the server-recorded failure's message/status when the probe provides one.
 */
export async function pollAnalysis({ start, probe, fetchResult, intervalMs = 5000, sleep = defaultSleep }) {
  const first = await start();
  if (!first.running) return first; // cache hit — done, shape untouched

  for (;;) {
    await sleep(intervalMs);
    const status = await probe();
    if (status.cached) return { ...(await fetchResult()), cached: false };
    if (!status.running) {
      const err = new Error(status.error?.message || 'the background analysis did not complete — re-run it');
      if (status.error?.status) err.status = status.error.status;
      throw err;
    }
  }
}
